import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOHelmetProps {
  pageType?: 'landing' | 'dashboard' | 'auth';
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string;
}

const SEOHelmet: React.FC<SEOHelmetProps> = ({ 
  pageType = 'landing',
  customTitle,
  customDescription,
  customKeywords
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  const title = customTitle || t('seo.title');
  const description = customDescription || t('seo.description');
  const keywords = customKeywords || t('seo.keywords');
  
  // Force title update when language changes
  useEffect(() => {
    document.title = title;
  }, [title, currentLanguage]);
  
  // Add language-specific URL structure for better SEO
  const canonicalUrl = window.location.origin + window.location.pathname;
  const alternateUrl = currentLanguage === 'en' 
    ? `${window.location.origin}/es${window.location.pathname}`
    : `${window.location.origin}${window.location.pathname.replace('/es', '')}`;
  
  return (
    <Helmet key={currentLanguage}>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Media */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="TenantTerms" />
      <meta property="og:locale" content={currentLanguage === 'es' ? 'es_US' : 'en_US'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Language and Geographic Targeting */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="California" />
      <meta name="geo.position" content="36.778259;-119.417931" />
      <meta name="ICBM" content="36.778259, -119.417931" />
      
      {/* Canonical and Alternate Language Links */}
      <link rel="canonical" href={canonicalUrl} />
      <link 
        rel="alternate" 
        hrefLang={currentLanguage === 'en' ? 'es' : 'en'} 
        href={alternateUrl} 
      />
      <link rel="alternate" hrefLang="x-default" href={window.location.origin} />
      
      {/* Schema.org Structured Data for Local Business */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "TenantTerms",
          "description": description,
          "url": canonicalUrl,
          "areaServed": "California",
          "serviceType": currentLanguage === 'es' 
            ? "Generación de Contratos de Arrendamiento"
            : "Lease Agreement Generation",
          "priceRange": "$1",
          "availableLanguage": ["English", "Spanish"],
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;